import ResourcesIds from "../resources/ResourcesIds";
import { localised } from './models/localisedText';
import { ServiceType } from "./models/service";
import { IPatientPurchase, PurchaseStatus } from "./models/patientPurchase";
import { TranslateFn } from "./validationRules";

/**
 * How a purchase is labelled on screen.
 *
 * Shared by the card, the status chip and the dialog, the same way membershipDisplay is
 * shared: a status that reads one way on the card and another way in the history would
 * be a bug rather than a style choice.
 */
export const PURCHASE_STATUS_LABELS: Record<PurchaseStatus, string> = {
    ACTIVE: ResourcesIds.PURCHASE_STATUS_ACTIVE,
    COMPLETED: ResourcesIds.PURCHASE_STATUS_COMPLETED,
    EXPIRED: ResourcesIds.PURCHASE_STATUS_EXPIRED,
    CANCELLED: ResourcesIds.PURCHASE_STATUS_CANCELLED,
};

/**
 * Warning rather than error for an expired package: it is the one to look at, but it has
 * not gone wrong -- the sessions are still there and honouring it is the therapist's
 * call.
 */
export const PURCHASE_STATUS_COLOURS: Record<
    PurchaseStatus,
    'success' | 'info' | 'warning' | 'default'
> = {
    ACTIVE: 'success',
    COMPLETED: 'info',
    EXPIRED: 'warning',
    CANCELLED: 'default',
};

export const SERVICE_TYPE_LABELS: Record<ServiceType, string> = {
    SINGLE: ResourcesIds.PURCHASE_TYPE_SINGLE,
    PACKAGE: ResourcesIds.PURCHASE_TYPE_PACKAGE,
};

/**
 * Money as the clinic writes it. Two decimals always, so a column of prices lines up and
 * 900 does not sit next to 899.50 looking like a different kind of number.
 */
export const formatPrice = (amount: number): string => amount.toFixed(2);

/**
 * The discount as a percentage of the list price, for display beside the amount.
 *
 * Rounded to a whole number: it is derived from two stored amounts, so anything finer is
 * arithmetic noise rather than what anybody agreed to. Zero list price yields 0 rather
 * than a division by zero -- a free service cannot be discounted.
 */
export const discountPercent = (listPrice: number, discountAmount: number): number =>
    listPrice > 0 ? Math.round((discountAmount / listPrice) * 100) : 0;

/**
 * The other direction: what a percentage off a list price comes to, and what is left to pay.
 *
 * The amount is rounded *first* and the total derived by subtracting it, never rounded
 * independently. A purchase row stores `listPrice` and `discountAmount` and the server
 * computes `listPrice - discountAmount` itself, so rounding the two ends separately would let
 * the form print a total the row does not hold -- 12% off 383.33 being the kind of sum where
 * the two answers differ by a ban.
 *
 * Bounded at both ends. A negative percentage is a surcharge and more than a hundred is a
 * price the clinic pays the patient; the schema refuses both, and so does the API, but this is
 * arithmetic and should not produce a figure nobody could act on while it is being typed.
 * Anything unparseable reads as no discount rather than as `NaN` on screen.
 */
export const priceBreakdown = (
    listPrice: number,
    percent: number
): { discountAmount: number; finalPrice: number } => {
    const list: number = Number.isFinite(listPrice) ? Math.max(listPrice, 0) : 0;
    const bounded: number = Number.isFinite(percent) ? Math.min(Math.max(percent, 0), 100) : 0;

    // `list * bounded` rather than `list * bounded / 100` rounded to two places: the same
    // number, without the intermediate float that makes 0.145 round the wrong way.
    const discountAmount: number = Math.round(list * bounded) / 100;

    return { discountAmount, finalPrice: list - discountAmount };
};

/**
 * How a plan is offered in a booking menu: the service, then what was bought of it --
 * "Kinetoterapie · 5 x 50 min".
 *
 * Read off the purchase, never looked up in the catalogue. A plan is a snapshot taken at
 * the sale and the priced line behind it is not: it can be retimed, repriced or dropped,
 * and a package's line is not in the bookable list at all, since `GET /services` answers
 * with singles unless asked otherwise. Resolving a package against that list therefore
 * missed every time, and the name-and-duration fallback behind the lookup then landed on
 * the *single* line of the same discipline -- so a five-session package read "1 x 50'",
 * which is another package's label rather than a vague one.
 *
 * Shared for the same reason the status labels above are: a plan that reads one way in
 * the patient's wizard and another way in the therapist's dialog is a bug, not a style.
 */
export const planLabel = (
    purchase: Pick<IPatientPurchase,
        'serviceNameRo' | 'serviceNameEn' | 'durationMinutes' | 'totalSessions'>,
    translate: TranslateFn,
    language: string
): string => {
    // A single is not "1 x 50 min" -- the count is only worth saying when there is more
    // than one of them, which is what makes a package a package.
    const sold: string = purchase.totalSessions > 1
        ? translate(ResourcesIds.PURCHASE_PLAN_SESSIONS, {
            sessions: purchase.totalSessions,
            minutes: purchase.durationMinutes,
        })
        : translate(ResourcesIds.PURCHASE_SESSION_DURATION, {
            minutes: purchase.durationMinutes,
        });

    // The service is the clinic's own words, so it goes through `localised` rather than
    // through `translate` -- a name the clinic never wrote in English shows in Romanian
    // rather than as a missing key.
    return `${localised(purchase.serviceNameRo, purchase.serviceNameEn, language)} · ${sold}`;
};
